import { Icon, Pill, Stack, Text, Tooltip } from '@sixfold/common-ui';
import React from 'react';
import { NavLink } from 'react-router';

import { CompanyRelationship } from '../../company/entities';
import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { CompanyRelationshipOriginType } from '../../lib/graphql/result_types';
import { Routes } from '../../routes';

export const ORIGIN_TYPE_ORDER = [
  CompanyRelationshipOriginType.COMPANY,
  CompanyRelationshipOriginType.PLATFORM,
  CompanyRelationshipOriginType.TOUR,
  CompanyRelationshipOriginType.COMPANY_GROUP_COPY,
];

export const getOriginTypeLabel = (type: CompanyRelationshipOriginType): string => {
  switch (type) {
    case CompanyRelationshipOriginType.COMPANY:
      return 'company';
    case CompanyRelationshipOriginType.PLATFORM:
      return 'platform';
    case CompanyRelationshipOriginType.TOUR:
      return 'tours';
    case CompanyRelationshipOriginType.COMPANY_GROUP_COPY:
      return 'company group';
    default: {
      const exhaustiveCheck: never = type;
      return exhaustiveCheck;
    }
  }
};

const getOriginTypeDescription = (type: CompanyRelationshipOriginType): string => {
  switch (type) {
    case CompanyRelationshipOriginType.COMPANY:
      return 'added by a user in customer-web or backoffice, through an accepted invite, a referral signup or a CLI import';
    case CompanyRelationshipOriginType.PLATFORM:
      return 'synced from a platform, e.g. Transporeon';
    case CompanyRelationshipOriginType.TOUR:
      return 'inferred from tours allocated to the carrier';
    case CompanyRelationshipOriginType.COMPANY_GROUP_COPY:
      return 'copied from another company group member';
    default: {
      const exhaustiveCheck: never = type;
      return exhaustiveCheck;
    }
  }
};

export const RelationshipOriginsTitle: React.FC = () => (
  <Stack direction="row" gap="xsmall" alignItems="center">
    Origin
    <Tooltip
      content={
        <ul>
          {ORIGIN_TYPE_ORDER.map((type) => (
            <li key={type}>
              <strong>{getOriginTypeLabel(type)}</strong> — {getOriginTypeDescription(type)}
            </li>
          ))}
        </ul>
      }>
      <Icon name="small-info" />
    </Tooltip>
  </Stack>
);

interface RelationshipOriginsProps {
  origins: Pick<CompanyRelationship['origins'][number], 'type'>[];
  copiedFrom: CompanyRelationship['companyGroupCopiedFrom'];
}

export const RelationshipOrigins: React.FC<RelationshipOriginsProps> = ({ origins, copiedFrom }) => {
  const originTypes = ORIGIN_TYPE_ORDER.filter((type) => origins.some((origin) => origin.type === type));

  if (originTypes.length === 0) {
    return <>-</>;
  }

  return (
    <Stack direction="column" gap="xsmall" alignItems="start">
      {originTypes.map((type) => (
        <React.Fragment key={type}>
          <Stack direction="row">
            <Pill kind={type === CompanyRelationshipOriginType.COMPANY_GROUP_COPY ? 'success' : 'neutral'}>
              {getOriginTypeLabel(type)}
            </Pill>
          </Stack>
          {type === CompanyRelationshipOriginType.COMPANY_GROUP_COPY &&
            copiedFrom.map(({ company, copiedAt }) => (
              <Text key={company.company_id} size="small">
                from{' '}
                <NavLink to={Routes.Company.generatePath({ company_id: company.company_id })}>
                  {company.company_name ?? company.company_id}
                </NavLink>{' '}
                (<FormattedDate date={copiedAt} showTime={false} showTimezone={false} />)
              </Text>
            ))}
        </React.Fragment>
      ))}
    </Stack>
  );
};
